import { observer } from "mobx-react"
import { Box, IconButton } from "@mui/material"
import { RadioButtonUnchecked, CheckBoxOutlineBlank } from "@mui/icons-material"

import { t } from "@lingui/macro"
import { useMemo } from "react"

import { IPollOptionsSection } from "./types"

import { ModalTextField } from "src/components/ModalTextField"
import { Cross16 } from "src/components/icons/Cross16"
import { StyledPollOptionStack } from "src/modals/polls/components/PollOptions/styled"
import { FieldTypeDropdownKeys } from "src/components/Inputs/types/IFieldTypeDropdown"
import { POLL_CONSTANTS } from "src/modals/polls/constants/constants"
import { useStore } from "src/store/lib/useStore"
import { PollModalStore } from "src/modals/polls/store"

export const PollOptions = observer(
    ({
        value,
        onChange,
        onRemove,
        disabled,
        showRemoveButton,
        index,
    }: IPollOptionsSection) => {
        const store = useStore(PollModalStore)
        const type = store.form.get("questionType")

        const icon = useMemo(() => {
            const Icon =
                type === FieldTypeDropdownKeys.RadioButton
                    ? RadioButtonUnchecked
                    : CheckBoxOutlineBlank

            return (
                <IconButton size="small" color="inherit" disabled={disabled}>
                    <Icon />
                </IconButton>
            )
        }, [disabled, type])

        const error = useMemo(
            () => Boolean(store.form.error("options")) && !Boolean(value),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [store.form.error("options"), value],
        )

        const helperText = error ? store.form.error("options") : undefined

        return (
            <StyledPollOptionStack key={index}>
                {icon}
                <Box sx={{ flex: 1 }}>
                    <ModalTextField
                        variant="default"
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        error={error}
                        helperText={helperText}
                        label={t`poll.question-config.option-label ${
                            index + 1
                        }`}
                        maxLength={POLL_CONSTANTS.MAX_OPTION_LENGTH}
                    />
                </Box>
                {Boolean(showRemoveButton) && (
                    <IconButton
                        size="small"
                        onClick={onRemove}
                        color="inherit"
                        disabled={disabled}
                    >
                        <Cross16 />
                    </IconButton>
                )}
            </StyledPollOptionStack>
        )
    },
)
